// src/components/organization/subscription/PendingPaymentCard.js
import React from "react";

const PendingPaymentCard = ({ pendingPayment, onRetryPayment }) => {
  if (!pendingPayment) return null;

  const {
    transactionId,
    amount,
    metadata = {},
    createdAt,
    expiresAt
  } = pendingPayment;

  const {
    packageName,
    packageId,
    isUpgrade,
    originalPrice,
    creditApplied,
    // eslint-disable-next-line no-unused-vars
    finalPrice,
    geideaResponse
  } = metadata;

  const paymentLink = geideaResponse?.paymentIntent?.link;
  // eslint-disable-next-line no-unused-vars
  const geideaExpiryDate = geideaResponse?.paymentIntent?.expiryDate;
  const qrCode = geideaResponse?.paymentIntent?.payLinkQrImage;

  // Use our backend expiration (24 hours) instead of Geidea's expiration (30 days)
  // Calculate 24 hours from creation if expiresAt is not available
  const get24HourExpiration = () => {
    if (expiresAt) return expiresAt;
    if (createdAt) {
      const created = new Date(createdAt);
      const expiry = new Date(created.getTime() + 24 * 60 * 60 * 1000);
      return expiry.toISOString();
    }
    return null;
  };

  const expiryDate = get24HourExpiration();

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimeRemaining = (expiryDate) => {
    if (!expiryDate) return null;
    
    const now = new Date();
    const expiry = new Date(expiryDate);
    const diff = expiry - now;
    
    if (diff <= 0) return null;
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m remaining`;
    } else {
      return `${minutes}m remaining`;
    }
  };

  const isExpired = expiryDate && new Date(expiryDate) < new Date();
  const timeRemaining = getTimeRemaining(expiryDate);

  const handleRetryPayment = () => {
    if (paymentLink && !isExpired) {
      window.open(paymentLink, '_blank');
    } else if (onRetryPayment) {
      onRetryPayment(packageId);
    }
  };

  return (
    <div className="max-w-5xl mx-auto mb-8">
      {/* Main Card with Enhanced Gradient */}
      <div className="relative bg-gradient-to-br from-amber-50 via-orange-50 to-red-50 border border-amber-200 rounded-2xl overflow-hidden shadow-2xl">
        {/* Decorative Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-32 h-32 bg-gradient-to-br from-orange-400 to-red-400 rounded-full -translate-x-16 -translate-y-16"></div>
          <div className="absolute bottom-0 right-0 w-40 h-40 bg-gradient-to-br from-yellow-400 to-orange-400 rounded-full translate-x-20 translate-y-20"></div>
        </div>
        
        {/* Status Banner */}
        <div className="relative bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/30">
                <span className="text-3xl">💳</span>
              </div>
              <div className="text-white">
                <h2 className="text-2xl font-bold tracking-tight">
                  {isUpgrade ? '⬆️ Upgrade Payment Pending' : '🚀 Subscription Payment Pending'}
                </h2>
                <p className="text-amber-100 font-medium">
                  Complete your payment to {isUpgrade ? 'upgrade your plan' : 'activate your subscription'}
                </p>
              </div>
            </div>
            <div className="text-right text-white">
              <div className="text-xs font-medium text-amber-100 uppercase tracking-wider">Transaction ID</div>
              <div className="font-mono text-sm bg-white/20 px-3 py-1 rounded-lg border border-white/30 mt-1">
                {transactionId}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="relative p-8">

          {/* Payment Details Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            {/* Package Info Card */}
            <div className="lg:col-span-2 bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-white/50 shadow-lg">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-white text-lg">📦</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900">Package Details</h3>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-700 font-medium">Package Name:</span>
                  <span className="font-bold text-gray-900 text-lg">{packageName || 'Unknown Package'}</span>
                </div>
                
                {isUpgrade && (
                  <>
                    <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                      <span className="text-blue-700 font-medium">Original Price:</span>
                      <span className="font-bold text-blue-900">{originalPrice} SAR</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                      <span className="text-green-700 font-medium">Credit Applied:</span>
                      <span className="font-bold text-green-600">-{creditApplied?.toFixed(2)} SAR</span>
                    </div>
                    <div className="border-t-2 border-dashed border-gray-300 my-2"></div>
                  </>
                )}
                
                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-orange-100 to-red-100 rounded-lg border-2 border-orange-200">
                  <span className="text-orange-800 font-bold text-lg">💰 Amount to Pay:</span>
                  <span className="font-black text-2xl text-orange-600">{amount} SAR</span>
                </div>
              </div>
            </div>

            {/* Payment Status Card */}
            <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-white/50 shadow-lg">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center mr-3">
                  <span className="text-white text-lg">📊</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900">Payment Status</h3>
              </div>
              
              <div className="space-y-4">
                                 <div className="text-center">
                   <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-yellow-400 to-orange-400 text-white rounded-full font-bold text-sm shadow-lg">
                     <span className="w-2 h-2 bg-white rounded-full mr-2 animate-pulse"></span>
                     PENDING PAYMENT
                   </div>
                   <p className="text-xs text-gray-600 mt-2">
                     Payment processing may take up to 8 hours
                   </p>
                 </div>
                
                                 <div className="space-y-3">
                   <div className="flex justify-between items-center">
                     <span className="text-gray-600 text-sm">Created:</span>
                     <span className="font-medium text-gray-900 text-sm">{formatDate(createdAt)}</span>
                   </div>
                   
                   {expiryDate && (
                     <div className="space-y-2">
                       <div className="flex justify-between items-center">
                         <span className="text-gray-600 text-sm">Expires:</span>
                         <span className={`font-medium text-sm ${isExpired ? 'text-red-600' : 'text-gray-900'}`}>
                           {formatDate(expiryDate)}
                         </span>
                       </div>
                       
                       {/* Time Remaining Display */}
                       {!isExpired && timeRemaining && (
                         <div className="bg-amber-50 border border-amber-200 rounded-lg p-2">
                           <div className="flex items-center justify-center space-x-2">
                             <span className="text-amber-600 text-lg">⏰</span>
                             <span className="text-amber-800 font-bold text-sm">{timeRemaining}</span>
                           </div>
                           <p className="text-amber-700 text-xs text-center mt-1">
                             Payment link expires after 24 hours
                           </p>
                         </div>
                       )}
                     </div>
                   )}
                 </div>
                
                                 {isExpired && (
                   <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                     <div className="text-center">
                       <p className="text-red-700 text-sm font-bold">
                         ⚠️ Payment Link Expired
                       </p>
                       <p className="text-red-600 text-xs mt-1">
                         Payment links expire after 24 hours for security
                       </p>
                     </div>
                   </div>
                 )}
              </div>
            </div>
          </div>

          {/* QR Code Section */}
          {qrCode && !isExpired && (
            <div className="mb-8 text-center">
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-white/50 shadow-lg inline-block">
                <div className="flex items-center justify-center mb-4">
                  <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-blue-500 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-white text-lg">📱</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">Scan to Pay</h3>
                </div>
                                 <div className="bg-white p-4 rounded-lg border-2 border-gray-200 shadow-inner">
                   <img 
                     src={qrCode.startsWith('data:') ? qrCode : `data:image/png;base64,${qrCode}`} 
                     alt="Payment QR Code" 
                     className="w-40 h-40 mx-auto"
                     onError={(e) => {
                       console.error('QR Code failed to load:', qrCode);
                       e.target.style.display = 'none';
                     }}
                   />
                 </div>
                                 <p className="text-gray-600 text-sm mt-3">Scan with your mobile banking app</p>
                 {timeRemaining && (
                   <div className="mt-3 px-3 py-1 bg-amber-100 rounded-full">
                     <p className="text-amber-800 text-xs font-medium text-center">
                       ⏰ {timeRemaining}
                     </p>
                   </div>
                 )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <button
              onClick={handleRetryPayment}
              className="group relative px-10 py-4 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-xl font-bold text-lg transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:scale-105 hover:from-orange-600 hover:to-red-600"
            >
              <span className="relative z-10 flex items-center justify-center">
                {paymentLink && !isExpired ? (
                  <>
                    <span className="mr-2">🔗</span>
                    Open Payment Link
                  </>
                ) : (
                  <>
                    <span className="mr-2">🔄</span>
                    Create New Payment Link
                  </>
                )}
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-orange-600 to-red-600 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </button>
          </div>

                     {/* Enhanced Instructions */}
           <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 shadow-lg">
             <div className="flex items-center mb-4">
               <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center mr-3">
                 <span className="text-white text-lg">💡</span>
               </div>
               <h4 className="text-xl font-bold text-blue-900">Payment Instructions</h4>
             </div>
             
             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
               <div className="space-y-3">
                 <div className="flex items-start space-x-3">
                   <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold mt-0.5">1</div>
                   <p className="text-blue-800 text-sm">Click the payment link button to complete your payment</p>
                 </div>
                 <div className="flex items-start space-x-3">
                   <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold mt-0.5">2</div>
                   <p className="text-blue-800 text-sm">You can also scan the QR code with your mobile banking app</p>
                 </div>
               </div>
               <div className="space-y-3">
                 <div className="flex items-start space-x-3">
                   <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold mt-0.5">3</div>
                   <p className="text-blue-800 text-sm">After payment, processing may take up to 8 hours</p>
                 </div>
                 <div className="flex items-start space-x-3">
                   <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold mt-0.5">4</div>
                   <p className="text-blue-800 text-sm">Your subscription will be activated automatically once processed</p>
                 </div>
               </div>
             </div>
             
             {/* Processing Time Notice */}
             <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
               <div className="flex items-start space-x-3">
                 <div className="w-6 h-6 bg-amber-500 text-white rounded-full flex items-center justify-center text-xs font-bold mt-0.5 flex-shrink-0">⏱️</div>
                 <div>
                   <h5 className="font-semibold text-amber-800 mb-1">Processing Time</h5>
                   <p className="text-amber-700 text-sm">
                     Payment processing typically takes 1-8 hours depending on your bank and payment method. 
                     You'll receive an email confirmation once your subscription is activated. 
                     Please be patient and avoid making duplicate payments.
                   </p>
                 </div>
               </div>
             </div>
           </div>
        </div>
      </div>
    </div>
  );
};

export default PendingPaymentCard; 